import { UNLIMITED_VALUE } from '@/constants/constants';
import { FeatureGroup, FeatureType } from '@/services/Features/features.service';
import { CreatePlanDTO, Plan } from '@/services/Plans/plans.service';
import { Options } from '../components/plans/FeatureGroupRestrictions';

const processFeature = (feature: any) => {
  const isBoolean = feature.type === FeatureType.BOOLEAN;

  let value = feature.value;
  let limit;

  if (isBoolean) {
    value = feature.value ?? '1';
    limit = feature.limit ?? '1';
    return {
      ...feature,
      value,
      limit,
    };
  }
  if (feature.type === 'number' && feature.value && feature.value != UNLIMITED_VALUE) {
    value = Options.LIMITED;
    limit = feature.value ?? '0';
  } else {
    value = Options.UNLIMITED;
  }
  return {
    ...feature,
    value,
    limit: limit ?? undefined,
  };
};

// Helper function to process a feature group
const processFeatureGroup = (group: FeatureGroup) => {
  const groupKey = group.name;
  const processedGroup: Record<string, any> = {};

  group.features.forEach((feature) => {
    processedGroup[feature.name] = processFeature(feature);
  });

  return { groupKey, processedGroup };
};

export const getDefaultPlanData = (
  allFeatureGroupsWithFeatures: FeatureGroup[],
): Record<string, any> => {
  const newFormData: Record<string, any> = {};

  allFeatureGroupsWithFeatures.forEach((group) => {
    const { groupKey, processedGroup } = processFeatureGroup(group);
    newFormData[groupKey] = processedGroup;
  });

  return {
    ...newFormData,
    extensions: null, // UNLIMITED by default
    checkoutFlows: null, // UNLIMITED by default
  };
};

export const getDefaultDataFromPlan = (plan: Plan) => {
  const newFormData: Record<string, any> = {
    name: plan.name,
    extensions: plan.extensions ? plan.extensions.map((ext) => ext._id) : null,
    checkoutFlows: plan.checkoutFlows ? plan.checkoutFlows.map((flow) => flow._id) : null,
  };

  const featureGroups = Object.values(plan?.featureGroups);
  featureGroups.forEach((group) => {
    const { groupKey, processedGroup } = processFeatureGroup(group as FeatureGroup);
    newFormData[groupKey] = processedGroup;
  });

  return newFormData;
};

interface CreatePlanFeature {
  guid: string;
  value: string;
  limit?: number;
}

interface FormData {
  [key: string]: {
    [key: string]: CreatePlanFeature;
  };
}

export const transformFormDataToFeatures = (
  data: FormData,
  featureGroupNames: string[],
): CreatePlanFeature[] => {
  const features: CreatePlanFeature[] = [];

  featureGroupNames.forEach((featureGroupName) => {
    const featureGroupData = data[featureGroupName];
    if (featureGroupData) {
      (Object.values(featureGroupData) as CreatePlanFeature[]).forEach((featureFromForm) => {
        features.push(featureFromForm);
      });
    }
  });

  return features;
};

export const createPlanData = (
  data: any,
  id: string,
  features: CreatePlanFeature[],
): CreatePlanDTO => {
  return {
    name: data.name,
    features: features,
    builds: data.checkoutFlows,
    organizationId: id,
    extensions: data.extensions,
  };
};
